import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';

// A persistent, unmissable bar shown whenever a support user is impersonating
// another user. Rendered above the router so it appears on every impersonated
// page (all three role layouts) — the way back to the support console.
const Bar = styled.div`
  position: sticky;
  top: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 16px;
  background-color: #b45309; /* amber-700 — clearly "you are not yourself" */
  color: #fff;
  font-size: 0.9rem;
  line-height: 1.3;
  text-align: center;
`;

const Label = styled.span`
  font-weight: 500;
`;

const StopButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.85);
  background: transparent;
  color: #fff;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  min-height: 32px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const ImpersonationBanner = () => {
  const { isImpersonating, currentUser, stopImpersonation } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stopping, setStopping] = useState(false);

  if (!isImpersonating || !currentUser) return null;

  const name = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email;

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopImpersonation();
      navigate('/support');
    } finally {
      setStopping(false);
    }
  };

  return (
    <Bar role="alert">
      <Label>{t('support.banner.viewingAs', { name, email: currentUser.email })}</Label>
      <StopButton type="button" onClick={handleStop} disabled={stopping}>
        {t('support.banner.stop')}
      </StopButton>
    </Bar>
  );
};

export default ImpersonationBanner;
